/**
 * OSD Homepage
 *
 * First screen shown to users at root URL (/)
 * Displays OSD selection with 4 cards (TAURON, PGE, ENEA, ENERGA)
 *
 * After selecting OSD, user navigates to /:osd/
 * Example: Clicking TAURON → /tauron/
 *
 * This page handles OAuth callbacks as well (Facebook login redirects to /)
 */

import { Container, Title, Text, Stack, Box } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { AuthCallback } from '../features/auth';
import { Layout } from '../shared/components/Layout';
import { OSDCards } from '../shared/components/OSDSelector';
import { useSEO } from '../shared/hooks';

export function OSDHomepage() {
  const [searchParams] = useSearchParams();

  useSEO({
    title: 'Porównaj taryfy prądu - Kalkulator energii 2026',
    description: 'Wybierz operatora sieci dystrybucyjnej i porównaj taryfy energetyczne. Sprawdź w 30 sekund czy przepłacasz za prąd.',
    canonicalPath: '/',
  });

  // Check if this is an OAuth callback (Facebook login redirects to / with params)
  const isOAuthCallback = searchParams.has('code') && searchParams.has('state');

  // If OAuth callback, show AuthCallback handler
  if (isOAuthCallback) {
    return <AuthCallback />;
  }

  // Otherwise, show OSD selection page
  return (
    <Layout isAuthenticated={false}>
      {/* Hero Section */}
      <Box
        py={80}
        style={{
          backgroundColor: 'var(--mantine-color-ecoGreen-0)',
        }}
      >
        <Container size="lg">
          <Stack gap="xl" align="center" ta="center">
            <Title order={1} size={44} fw={800} lh={1.2}>
              Porównaj taryfy prądu{' '}
              <span style={{ color: 'var(--mantine-color-ecoGreen-6)' }}>
                w 30 sekund
              </span>
            </Title>

            <Text size="xl" c="dimmed" maw={700}>
              Wybierz swojego operatora sieci dystrybucyjnej (OSD), aby zobaczyć
              dostępne taryfy i porównać koszty energii elektrycznej.
            </Text>

            <Text size="md" c="dimmed">
              Nie wiesz, który operator obsługuje Twój region?{' '}
              <Text component="span" fw={600} c="ecoGreen">
                Sprawdź na swojej fakturze za prąd
              </Text>
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* OSD Selection */}
      <Box py={60} style={{ backgroundColor: 'white' }}>
        <Container size="lg">
          <OSDCards title="Wybierz swojego operatora OSD" />
        </Container>
      </Box>

      {/* Info Section */}
      <Box
        py={60}
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)',
        }}
      >
        <Container size="md">
          <Stack gap="lg" align="center" ta="center">
            <Title order={2} size="h3">
              Czym jest OSD?
            </Title>

            <Text size="md" c="dimmed" maw={600}>
              <strong>Operator Systemu Dystrybucyjnego (OSD)</strong> to firma
              odpowiedzialna za dostarczanie energii elektrycznej do Twojego domu.
              W Polsce działają 4 główne operatorzy: TAURON, PGE, ENEA i ENERGA.
            </Text>

            <Text size="md" c="dimmed" maw={600}>
              Każdy operator oferuje różne taryfy i ceny. Wybór odpowiedniej taryfy
              może zaoszczędzić Ci nawet <strong>340 zł rocznie</strong>.
            </Text>
          </Stack>
        </Container>
      </Box>
    </Layout>
  );
}
